/**
 * TalentSearchTab — Verified Partner firm's view onto the Reverse
 * Job Board. Tier-gated to mid/large plan-size; small-plan and
 * unverified firms see the locked teaser with the aggregate count.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import UniversityAutocomplete from "@/components/app/UniversityAutocomplete";
import DegreeAutocomplete from "@/components/app/DegreeAutocomplete";
import CityAutocomplete from "@/components/app/CityAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Handshake,
  Loader2,
  Lock,
  Sparkles,
  UserRound,
  Mail,
  Trash2,
  Linkedin,
  Globe,
  Award,
  Megaphone,
  Link2,
} from "lucide-react";


// ── Shareable preset URLs ──────────────────────────────────────
// Serialise the current filter grid (multi-select include arrays +
// their `_not` exclusion counterparts, plus scalar fields and the
// sort-by-fit toggle) into a compact base64url-encoded JSON blob
// under `?preset=…`. On page load we hydrate the filter state from
// that param BEFORE the first browse call fires, so the recipient
// gets an identical result set. The recipient still hits the
// firm-owner auth guard first; the query string survives that
// redirect via react-router's `location` object.
const DEFAULT_FILTERS = {
  sectors: [], sectors_not: [],
  skills: [], skills_not: [],
  grad: "", university: null,
  subject_families: [], subject_families_not: [],
  degree: null,
  languages: [], languages_not: [],
  notable_extras: [], notable_extras_not: [],
  diversity_tags: [], diversity_tags_not: [],
  status: "",
  cities: [], cities_not: [],
};

const _b64urlEncode = (s) => {
  // btoa only handles latin1 — round-trip through UTF-8 so any
  // non-ASCII university/degree names survive.
  const utf8 = unescape(encodeURIComponent(s));
  return btoa(utf8).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
const _b64urlDecode = (s) => {
  const pad = s.length % 4 === 2 ? "==" : s.length % 4 === 3 ? "=" : "";
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  return decodeURIComponent(escape(atob(b64)));
};

// Strip empty arrays / falsy scalars to keep the URL short. The
// decoder re-fills defaults for missing keys.
const _encodePresetToken = (filters, sortByFit) => {
  const payload = {};
  for (const [k, v] of Object.entries(filters)) {
    if (Array.isArray(v)) { if (v.length) payload[k] = v; continue; }
    if (v && typeof v === "object") { if (v.id) payload[k] = { id: v.id, name: v.name || "" }; continue; }
    if (v) payload[k] = v;
  }
  if (sortByFit) payload._sort = "fit";
  return _b64urlEncode(JSON.stringify(payload));
};

const _decodePresetToken = (token) => {
  const p = JSON.parse(_b64urlDecode(token));
  const arr = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  return {
    filters: {
      sectors: arr(p.sectors), sectors_not: arr(p.sectors_not),
      skills: arr(p.skills), skills_not: arr(p.skills_not),
      grad: typeof p.grad === "string" ? p.grad : "",
      university: p.university && p.university.id ? p.university : null,
      subject_families: arr(p.subject_families), subject_families_not: arr(p.subject_families_not),
      degree: p.degree && p.degree.id ? p.degree : null,
      languages: arr(p.languages), languages_not: arr(p.languages_not),
      notable_extras: arr(p.notable_extras), notable_extras_not: arr(p.notable_extras_not),
      diversity_tags: arr(p.diversity_tags), diversity_tags_not: arr(p.diversity_tags_not),
      status: typeof p.status === "string" ? p.status : "",
      cities: arr(p.cities), cities_not: arr(p.cities_not),
    },
    sortByFit: p._sort === "fit",
  };
};

// Read `?preset=…` from window.location on mount. Returns null
// (default filters) on any parse failure so a bad link doesn't
// blank the tab.
const _readPresetFromUrl = () => {
  try {
    const p = new URLSearchParams(window.location.search).get("preset");
    if (!p) return null;
    return _decodePresetToken(p);
  } catch { return null; }
};


function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}


const NOTABLE_LABELS = {
  // Work experience & career
  internship_experience: "Internship experience",
  part_time_job_2yr_plus: "Part-time job 2yr+",
  freelance_client_work: "Freelance client work",
  startup_founder: "Founded a startup",
  family_business_experience: "Family business",
  // Research & academic
  published_research: "Published research",
  research_assistant: "Research assistant",
  academic_scholarship: "Academic scholarship",
  olympiad_medallist: "Olympiad medallist",
  language_olympiad: "Language olympiad",
  // Competitions & prizes
  hackathon_prize: "Hackathon prize",
  coding_contest: "Coding contest",
  case_competition_winner: "Case-comp winner",
  debate_champion: "Debate champion",
  moot_court_finalist: "Moot court finalist",
  // Leadership & community
  society_president: "Society president",
  student_union_officer: "SU officer",
  editor_student_paper: "Editor, student paper",
  peer_mentor: "Peer mentor",
  volunteering_1yr_plus: "Volunteering 1yr+",
  // Sports
  sports_captain: "Sports captain",
  national_team_athlete: "National team athlete",
  duke_of_edinburgh_gold: "DofE Gold",
  // Arts & performance
  musical_ensemble_principal: "Music ensemble principal",
  theatre_lead_role: "Theatre lead role",
  published_artist_writer: "Published artist / writer",
  // Life & service
  military_service: "Military service",
  first_generation_uni: "First-gen uni",
};


const DIVERSITY_LABELS = {
  gender_woman: "Women",
  gender_nonbinary: "Non-binary",
  gender_trans: "Trans",
  ethnicity_black: "Black",
  ethnicity_asian_south: "Asian (South)",
  ethnicity_asian_east_southeast: "Asian (East / SE)",
  ethnicity_mixed: "Mixed heritage",
  ethnicity_hispanic_latino: "Hispanic / Latino",
  ethnicity_mena: "Middle Eastern / N. African",
  ethnicity_indigenous_first_nations: "Indigenous / First Nations",
  first_gen_uni: "First-gen uni",
  state_school_educated: "State-school educated",
  low_income_household: "Low-income household",
  care_leaver: "Care leaver",
  refugee_asylum: "Refugee / asylum background",
  lgbtq_plus: "LGBTQ+",
  disability: "Disability",
  neurodivergent: "Neurodivergent",
  veteran_or_military_family: "Veteran / military family",
  mature_student: "Mature student",
  carer_responsibilities: "Carer responsibilities",
  faith_religion: "Faith / religion",
};


// Same label map used student-side on TalentProfile.jsx — kept in
// sync manually. If we ever diverge, students see one label and
// firms see a different one for the same key, which is confusing.
const SECTOR_LABELS = {
  consulting: "Consulting",
  finance: "Finance",
  tech: "Tech",
  law: "Law",
  engineering: "Engineering",
  medicine: "Medicine",
  policy_gov: "Policy / Gov",
  media_creative: "Media / Creative",
  science_research: "Science / Research",
  energy_climate: "Energy / Climate",
  retail_fmcg: "Retail / FMCG",
  aerospace_defence: "Aerospace / Defence",
  startup: "Startup",
  nonprofit: "Nonprofit",
  education: "Education",
};


/**
 * FilterChip — shared pill used by every multi-select control.
 *
 * When `exclude` is true the chip renders in rose (NOT-red) with a
 * "NOT " prefix and a distinct testid so tests can find only the
 * excluded ones. The `≠` button flips include ↔ exclude; the `×`
 * button removes the value entirely.
 */
function FilterChip({ label, exclude, onFlip, onRemove, testId }) {
  const cls = exclude
    ? "border-rose-500 bg-rose-50 text-rose-700"
    : "border-[#4C1D95] bg-[#F5F0FF] text-[#4C1D95]";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${cls}`}
    >
      {exclude && <span className="font-mono text-[10px]">NOT</span>}
      {label}
      {onFlip && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onFlip(); }}
          aria-label={exclude ? "Include instead" : "Exclude instead"}
          title={exclude ? "Include instead" : "Exclude instead"}
          data-testid={`${testId}-flip`}
          className="opacity-60 hover:opacity-100 font-mono text-[10px]"
        >
          ≠
        </button>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        aria-label={`Remove ${label}`}
        data-testid={`${testId}-remove`}
        className="hover:opacity-80"
      >
        ×
      </button>
    </span>
  );
}


/**
 * SuggestAutocomplete — generic typeahead over a suggestion endpoint.
 *
 * Used for firm-side "skill", "language" and "target city" filters.
 * These are free-text on the student side, so there's no canonical
 * taxonomy. This component surfaces the DISTINCT values that
 * actually exist across profiles today, sorted by frequency, and
 * still lets the firm type a niche fragment (Enter commits
 * whatever's in the box).
 *
 * MULTI-SELECT MODE (`multi` prop): renders selected values as
 * removable chips inside the trigger area and pushes further picks
 * onto the array. Backspace on an empty input removes the last
 * chip. This matches how modern search boxes (LinkedIn Recruiter,
 * Notion) handle multi-value tag inputs.
 *
 * Props:
 *   endpoint   — API path, e.g. "/talent/skills/suggest"
 *   labelKey   — property on each response item to render
 *   value      — string (single) OR string[] (multi)
 *   onChange   — (string) => void (single) OR (string[]) => void (multi)
 *   multi      — bool, default false
 *   placeholder
 *   testId     — root data-testid; children get suffixed with -input, -menu, -option-KEY, -chip-KEY
 */
function SuggestAutocomplete({ endpoint, labelKey, value, notValues, onChange, onFlip, onRemove, multi = false, placeholder, testId }) {
  const values = multi ? (Array.isArray(value) ? value : []) : null;
  const notList = multi ? (Array.isArray(notValues) ? notValues : []) : [];
  const [query, setQuery] = useState(multi ? "" : (value || ""));
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!multi) setQuery(value || "");
  }, [value, multi]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `${endpoint}?q=${encodeURIComponent(query)}&limit=8`
        );
        // Endpoints return either `{items: [...]}` (skills, languages)
        // or a bare array (cities). Normalise so this component
        // doesn't care about the wire shape.
        const raw = Array.isArray(data) ? data : (data?.items || []);
        setItems(raw);
        setActiveIdx(0);
      } catch {
        setItems([]);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [query, endpoint]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const commit = (v) => {
    const trimmed = (v || "").trim();
    if (!trimmed) { setOpen(false); return; }
    if (multi) {
      // Push unless already present (case-insensitive dedupe).
      const exists = values.some((x) => x.toLowerCase() === trimmed.toLowerCase());
      if (!exists) onChange([...values, trimmed]);
      setQuery("");
    } else {
      setQuery(trimmed);
      onChange(trimmed);
    }
    setOpen(false);
  };

  const removeChip = (idx) => {
    if (!multi) return;
    onChange(values.filter((_, i) => i !== idx));
  };

  const onKey = (e) => {
    if (multi && e.key === "Backspace" && !query && values.length) {
      // Backspace on empty input peels the last chip — same UX as
      // GitHub / Linear tag inputs.
      e.preventDefault();
      onChange(values.slice(0, -1));
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const pick = items[activeIdx]?.[labelKey] || query;
      commit(pick);
    } else if (e.key === "Escape") { setOpen(false); }
  };

  // In multi mode we render chips + a bare inline input inside a
  // shared bordered "trigger". In single mode we keep the plain
  // Input for backwards compatibility with existing tests.
  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      {multi ? (
        <div
          onClick={() => setOpen(true)}
          className="min-h-10 flex flex-wrap items-center gap-1 rounded-md border border-stone-300 bg-white px-2 py-1 focus-within:border-[#4C1D95] focus-within:ring-1 focus-within:ring-[#4C1D95]"
        >
          {values.map((v, i) => (
            <FilterChip
              key={`inc-${v}-${i}`}
              label={v}
              exclude={false}
              testId={`${testId}-chip-${v}`}
              onFlip={onFlip ? () => onFlip(v, false) : undefined}
              onRemove={() => (onRemove ? onRemove(v, false) : removeChip(i))}
            />
          ))}
          {notList.map((v, i) => (
            <FilterChip
              key={`exc-${v}-${i}`}
              label={v}
              exclude={true}
              testId={`${testId}-chip-not-${v}`}
              onFlip={onFlip ? () => onFlip(v, true) : undefined}
              onRemove={() => onRemove ? onRemove(v, true) : null}
            />
          ))}
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            placeholder={values.length + notList.length === 0 ? placeholder : ""}
            data-testid={`${testId}-input`}
            className="flex-1 min-w-[8ch] bg-transparent border-0 outline-none text-sm py-1"
          />
        </div>
      ) : (
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
        />
      )}
      {open && items.length > 0 && (
        <ul
          className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-md border border-stone-200 bg-white shadow-lg text-sm"
          data-testid={`${testId}-menu`}
        >
          {items.map((it, idx) => (
            <li
              key={it[labelKey]}
              onMouseDown={(e) => { e.preventDefault(); commit(it[labelKey]); }}
              className={
                "px-3 py-1.5 cursor-pointer flex items-center justify-between " +
                (idx === activeIdx ? "bg-[#F5F0FF] text-[#4C1D95]" : "hover:bg-stone-50")
              }
              data-testid={`${testId}-option-${it[labelKey]}`}
            >
              <span>{it[labelKey]}</span>
              {typeof it.count === "number" && (
                <span className="text-[10px] font-mono text-stone-400">×{it.count}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/**
 * MultiEnumPicker — chip-list + dropdown for enum filters where the
 * value set is closed (sector, notable_extra, diversity_tag,
 * subject_family). Same visual shape as SuggestAutocomplete's multi
 * mode so the whole filter grid feels uniform.
 *
 * Props:
 *   options   — Array<{ key, label }>
 *   values    — string[] (list of selected keys)
 *   onChange  — (string[]) => void
 *   placeholder — text shown when no chips
 *   testId    — root data-testid
 */
function MultiEnumPicker({ options, values, notValues, onChange, onFlip, onRemove, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const list = values || [];
  const notList = notValues || [];

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = (k) => {
    // Clicking an option that's currently EXCLUDED promotes it to
    // included; clicking an included option removes it; clicking a
    // fresh option adds it as included. `flip` (the chip's ≠ button)
    // is what moves an included value → excluded.
    if (notList.includes(k)) {
      // Move from exclude list to include list
      onFlip && onFlip(k, true);
      return;
    }
    if (list.includes(k)) onChange(list.filter((x) => x !== k));
    else onChange([...list, k]);
  };
  const labelOf = (k) =>
    (options.find((o) => o.key === k) || {}).label || k;

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div
        onClick={() => setOpen((v) => !v)}
        className="min-h-10 flex flex-wrap items-center gap-1 rounded-md border border-stone-300 bg-white px-2 py-1 cursor-pointer hover:border-stone-400"
        data-testid={`${testId}-trigger`}
      >
        {list.length === 0 && notList.length === 0 ? (
          <span className="text-sm text-stone-400 py-1">{placeholder}</span>
        ) : (
          <>
            {list.map((k) => (
              <FilterChip
                key={`inc-${k}`}
                label={labelOf(k)}
                exclude={false}
                testId={`${testId}-chip-${k}`}
                onFlip={onFlip ? () => onFlip(k, false) : undefined}
                onRemove={() => onRemove ? onRemove(k, false) : onChange(list.filter((x) => x !== k))}
              />
            ))}
            {notList.map((k) => (
              <FilterChip
                key={`exc-${k}`}
                label={labelOf(k)}
                exclude={true}
                testId={`${testId}-chip-not-${k}`}
                onFlip={onFlip ? () => onFlip(k, true) : undefined}
                onRemove={() => onRemove ? onRemove(k, true) : null}
              />
            ))}
          </>
        )}
        <span className="ml-auto text-stone-400">▾</span>
      </div>
      {open && (
        <ul
          className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border border-stone-200 bg-white shadow-lg text-sm"
          data-testid={`${testId}-menu`}
        >
          {options.map((o) => {
            const on = list.includes(o.key);
            const off = notList.includes(o.key);
            return (
              <li
                key={o.key}
                onMouseDown={(e) => { e.preventDefault(); toggle(o.key); }}
                className={
                  "px-3 py-1.5 cursor-pointer flex items-center gap-2 " +
                  (on ? "bg-[#F5F0FF] text-[#4C1D95]"
                     : off ? "bg-rose-50 text-rose-700"
                     : "hover:bg-stone-50")
                }
                data-testid={`${testId}-opt-${o.key}`}
              >
                <span
                  className={
                    "inline-block w-3.5 h-3.5 border rounded-sm " +
                    (on
                      ? "bg-[#4C1D95] border-[#4C1D95]"
                      : off
                        ? "bg-rose-500 border-rose-500"
                        : "bg-white border-stone-300")
                  }
                />
                {o.label}
                {off && <span className="ml-auto text-[10px] font-mono opacity-70">NOT</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


/**
 * FilterChipsSummary — read-only strip under the filter grid that
 * shows every currently-active filter as a coloured chip with an ✕
 * to clear just that one. Makes it obvious WHY the result count is
 * what it is, and matches how modern search UIs (Airbnb, Notion,
 * Perplexity) surface active filters. Renders nothing when no
 * filters are active.
 */
function FilterChipsSummary({
  filters, setFilters,
  allowedSectors, subjectFamilies, allowedNotable, allowedDiversity,
}) {
  const chips = [];
  const push = (key, label, clear, exclude = false) => chips.push({ key, label, clear, exclude });
  // Emit one chip per selected value so a firm can drop just ONE
  // sector out of {consulting, finance, tech} without wiping the
  // whole filter. Excluded values get a rose-red chip with a "NOT"
  // prefix so it's visually obvious what's being kept OUT.
  const pushMany = (baseKey, list, formatLabel, dropValue, exclude = false) => {
    (list || []).forEach((v, i) => push(
      `${baseKey}${exclude ? "-not" : ""}-${v}-${i}`,
      formatLabel(v),
      () => dropValue(v),
      exclude,
    ));
  };

  pushMany("sector", filters.sectors,
    (v) => `Sector: ${SECTOR_LABELS[v] || v}`,
    (v) => setFilters({ ...filters, sectors: filters.sectors.filter((x) => x !== v) }));
  pushMany("sector", filters.sectors_not,
    (v) => `Sector: ${SECTOR_LABELS[v] || v}`,
    (v) => setFilters({ ...filters, sectors_not: filters.sectors_not.filter((x) => x !== v) }), true);
  pushMany("skill", filters.skills,
    (v) => `Skill: ${v}`,
    (v) => setFilters({ ...filters, skills: filters.skills.filter((x) => x !== v) }));
  pushMany("skill", filters.skills_not,
    (v) => `Skill: ${v}`,
    (v) => setFilters({ ...filters, skills_not: filters.skills_not.filter((x) => x !== v) }), true);
  if (filters.grad) {
    push("grad", `Grad: ${filters.grad}`,
      () => setFilters({ ...filters, grad: "" }));
  }
  if (filters.university?.name) {
    push("university", `Uni: ${filters.university.name}`,
      () => setFilters({ ...filters, university: null, degree: null }));
  }
  if (filters.degree?.id) {
    push("degree",
      `Course: ${filters.degree.title || filters.degree.id}`,
      () => setFilters({ ...filters, degree: null }));
  }
  const famLabel = (v) => {
    const fam = subjectFamilies.find((f) => f.key === v);
    return `Degree family: ${fam?.label || v}`;
  };
  pushMany("subject_family", filters.subject_families, famLabel,
    (v) => setFilters({ ...filters, subject_families: filters.subject_families.filter((x) => x !== v) }));
  pushMany("subject_family", filters.subject_families_not, famLabel,
    (v) => setFilters({ ...filters, subject_families_not: filters.subject_families_not.filter((x) => x !== v) }), true);
  pushMany("language", filters.languages,
    (v) => `Language: ${v}`,
    (v) => setFilters({ ...filters, languages: filters.languages.filter((x) => x !== v) }));
  pushMany("language", filters.languages_not,
    (v) => `Language: ${v}`,
    (v) => setFilters({ ...filters, languages_not: filters.languages_not.filter((x) => x !== v) }), true);
  pushMany("notable_extra", filters.notable_extras,
    (v) => `Extra: ${NOTABLE_LABELS[v] || v}`,
    (v) => setFilters({ ...filters, notable_extras: filters.notable_extras.filter((x) => x !== v) }));
  pushMany("notable_extra", filters.notable_extras_not,
    (v) => `Extra: ${NOTABLE_LABELS[v] || v}`,
    (v) => setFilters({ ...filters, notable_extras_not: filters.notable_extras_not.filter((x) => x !== v) }), true);
  pushMany("diversity_tag", filters.diversity_tags,
    (v) => `Diversity: ${DIVERSITY_LABELS[v] || v}`,
    (v) => setFilters({ ...filters, diversity_tags: filters.diversity_tags.filter((x) => x !== v) }));
  pushMany("diversity_tag", filters.diversity_tags_not,
    (v) => `Diversity: ${DIVERSITY_LABELS[v] || v}`,
    (v) => setFilters({ ...filters, diversity_tags_not: filters.diversity_tags_not.filter((x) => x !== v) }), true);
  if (filters.status) {
    push("status", filters.status === "current" ? "At university" : "Applying",
      () => setFilters({ ...filters, status: "" }));
  }
  pushMany("city", filters.cities,
    (v) => `City: ${v}`,
    (v) => setFilters({ ...filters, cities: filters.cities.filter((x) => x !== v) }));
  pushMany("city", filters.cities_not,
    (v) => `City: ${v}`,
    (v) => setFilters({ ...filters, cities_not: filters.cities_not.filter((x) => x !== v) }), true);

  if (chips.length === 0) return null;

  const clearAll = () => setFilters({ ...DEFAULT_FILTERS });

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap py-1"
      data-testid="firm-portal-talent-filter-chips"
    >
      <span className="text-[10px] font-underground-sc tracking-widest text-stone-500 mr-1">
        Filters:
      </span>
      {chips.map((c) => (
        <span
          key={c.key}
          data-testid={`firm-portal-talent-filter-chip-${c.key}`}
          className={
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] " +
            (c.exclude
              ? "border-rose-500 bg-rose-50 text-rose-700"
              : "border-[#4C1D95] bg-[#F5F0FF] text-[#4C1D95]")
          }
        >
          {c.exclude && <span className="font-mono text-[10px]">NOT</span>}
          {c.label}
          <button
            type="button"
            onClick={c.clear}
            aria-label={`Clear ${c.key}`}
            data-testid={`firm-portal-talent-filter-chip-${c.key}-clear`}
            className="hover:opacity-80"
          >
            ×
          </button>
        </span>
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={clearAll}
          data-testid="firm-portal-talent-filter-chips-clear-all"
          className="ml-1 text-[11px] text-stone-500 hover:text-[#4C1D95] underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
}


export default function TalentSearchTab({ firm, onGotoUpgrade }) {
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [aggregate, setAggregate] = useState(0);
  const [rows, setRows] = useState([]);
  const [interests, setInterests] = useState([]);
  const [allowedNotable, setAllowedNotable] = useState([]);
  const [allowedDiversity, setAllowedDiversity] = useState([]);
  const [allowedSectors, setAllowedSectors] = useState([]);
  const [subjectFamilies, setSubjectFamilies] = useState([]); // [{key,label}]
  const [filters, setFilters] = useState(() => {
    // Lazy init from `?preset=…` so the first browse call already
    // uses the shared filters — no post-mount setFilters race.
    const shared = _readPresetFromUrl();
    return shared ? { ...DEFAULT_FILTERS, ...shared.filters } : { ...DEFAULT_FILTERS };
  });
  const [sortByFit, setSortByFit] = useState(() => _readPresetFromUrl()?.sortByFit || false);
  // Saved filter presets — CRUD wired to /firm-portal/{slug}/filter-presets.
  const [presets, setPresets] = useState([]);
  const [presetPickerOpen, setPresetPickerOpen] = useState(false);
  const [presetSaveOpen, setPresetSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  // When true, applying a preset UNIONs its arrays into current
  // filters instead of replacing. Lets a recruiter stack two saved
  // searches ("London · Law" ∪ "Manchester · Consulting") in one
  // click. Scalar fields (grad, uni, degree, status) still take
  // whichever side has a non-empty value; ties favour current.
  const [presetUnion, setPresetUnion] = useState(false);
  const [broadcast, setBroadcast] = useState({
    open: false, tag: "", message: "", programme_url: "",
    preview: null, submitting: false,
  });

  // Fetch enum labels once so backend + frontend stay in sync.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/talent/enums");
        setAllowedNotable(data?.allowed_notable_extras || []);
        setAllowedDiversity(data?.allowed_diversity_tags || []);
        setAllowedSectors(data?.allowed_sectors || []);
        setSubjectFamilies(data?.subject_families || []);
      } catch { /* silent — filter dropdowns just render empty */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ firm_slug: firm.slug });
      // Multi-value filters ship as comma-separated values; the
      // backend splits and dedupes before running an `$in` query.
      const csv = (arr) => (arr || []).join(",");
      if (filters.sectors?.length) q.set("sector", csv(filters.sectors));
      if (filters.sectors_not?.length) q.set("sector_not", csv(filters.sectors_not));
      if (filters.skills?.length) q.set("skill", csv(filters.skills));
      if (filters.skills_not?.length) q.set("skill_not", csv(filters.skills_not));
      if (filters.grad) q.set("graduation_year", filters.grad);
      if (filters.university?.id) q.set("university_id", filters.university.id);
      if (filters.subject_families?.length) q.set("subject_family", csv(filters.subject_families));
      if (filters.subject_families_not?.length) q.set("subject_family_not", csv(filters.subject_families_not));
      if (filters.degree?.id) q.set("degree_id", filters.degree.id);
      if (filters.languages?.length) q.set("language", csv(filters.languages));
      if (filters.languages_not?.length) q.set("language_not", csv(filters.languages_not));
      if (filters.notable_extras?.length) q.set("notable_extra", csv(filters.notable_extras));
      if (filters.notable_extras_not?.length) q.set("notable_extra_not", csv(filters.notable_extras_not));
      if (filters.diversity_tags?.length) q.set("diversity_tag", csv(filters.diversity_tags));
      if (filters.diversity_tags_not?.length) q.set("diversity_tag_not", csv(filters.diversity_tags_not));
      if (filters.status) q.set("status", filters.status);
      if (filters.cities?.length) q.set("city", csv(filters.cities));
      if (filters.cities_not?.length) q.set("city_not", csv(filters.cities_not));
      const [{ data: b }, { data: i }] = await Promise.all([
        api.get(`/talent/browse?${q}`),
        api.get("/talent/interests/mine"),
      ]);
      setRows(b.items || []);
      setInterests((i.as_firm || []).filter(r => r.firm_slug === firm.slug));
      setLocked(false);
    } catch (e) {
      if (e?.response?.status === 402) {
        setLocked(true);
        setAggregate(e.response.data?.detail?.aggregate_available || 0);
      } else {
        toast.error(e?.response?.data?.detail || "Could not load talent");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [firm.slug]);

  const requestIntro = async (profileId) => {
    try {
      const { data } = await api.post("/talent/interest/firm", {
        firm_slug: firm.slug,
        profile_id: profileId,
      });
      toast.success(data.mutual ? "Mutual match! Contact revealed below." : "Intro requested");
      const { data: i } = await api.get("/talent/interests/mine");
      setInterests((i.as_firm || []).filter(r => r.firm_slug === firm.slug));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const withdrawIntro = async (interestId) => {
    try {
      await api.delete(`/talent/interest?id=${encodeURIComponent(interestId)}`);
      const { data: i } = await api.get("/talent/interests/mine");
      setInterests((i.as_firm || []).filter(r => r.firm_slug === firm.slug));
      toast.success("Withdrawn");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // ── Filter presets ────────────────────────────────────────────
  // Load on mount + after every save/delete so the picker stays
  // fresh without a page refresh.
  const reloadPresets = async () => {
    try {
      const { data } = await api.get(`/firm-portal/${firm.slug}/filter-presets`);
      setPresets(data?.items || []);
    } catch { /* silent — presets are optional */ }
  };
  useEffect(() => { reloadPresets(); }, [firm.slug]); // eslint-disable-line

  // Factory for a (flip, remove) callback pair scoped to a specific
  // (includeKey, excludeKey) pair on `filters`. Passed to each
  // multi-select filter so its chips can flip include ↔ exclude and
  // remove themselves without every call-site knowing the state
  // shape. e.g. handlersFor("sectors", "sectors_not").
  const handlersFor = (incKey, excKey) => ({
    onFlip: (v, wasExcluded) => setFilters((f) => {
      if (wasExcluded) {
        return {
          ...f,
          [excKey]: (f[excKey] || []).filter((x) => x !== v),
          [incKey]: [...(f[incKey] || []), v],
        };
      }
      return {
        ...f,
        [incKey]: (f[incKey] || []).filter((x) => x !== v),
        [excKey]: [...(f[excKey] || []), v],
      };
    }),
    onRemove: (v, isExcluded) => setFilters((f) => ({
      ...f,
      [isExcluded ? excKey : incKey]: (f[isExcluded ? excKey : incKey] || []).filter((x) => x !== v),
    })),
  });

  const applyPreset = (preset) => {    if (!preset?.filters) return;
    const f = preset.filters || {};
    const arr = (v) => Array.isArray(v) ? v : (v ? [v] : []);
    // Union two arrays with case-insensitive dedupe. Used when the
    // Union toggle is on so stacking two presets doesn't create
    // duplicate chips.
    const merge = (a, b) => {
      const out = [...a];
      const seen = new Set(a.map((x) => (x || "").toLowerCase()));
      for (const x of b) {
        const k = (x || "").toLowerCase();
        if (!seen.has(k)) { out.push(x); seen.add(k); }
      }
      return out;
    };
    const next = {
      sectors: arr(f.sectors ?? f.sector),
      sectors_not: arr(f.sectors_not),
      skills: arr(f.skills ?? f.skill),
      skills_not: arr(f.skills_not),
      grad: f.grad || "",
      university: f.university || null,
      subject_families: arr(f.subject_families ?? f.subject_family),
      subject_families_not: arr(f.subject_families_not),
      degree: f.degree || null,
      languages: arr(f.languages ?? f.language),
      languages_not: arr(f.languages_not),
      notable_extras: arr(f.notable_extras ?? f.notable_extra),
      notable_extras_not: arr(f.notable_extras_not),
      diversity_tags: arr(f.diversity_tags ?? f.diversity_tag),
      diversity_tags_not: arr(f.diversity_tags_not),
      status: f.status || "",
      cities: arr(f.cities ?? f.city),
      cities_not: arr(f.cities_not),
    };
    if (presetUnion) {
      setFilters((cur) => ({
        sectors: merge(cur.sectors, next.sectors),
        sectors_not: merge(cur.sectors_not, next.sectors_not),
        skills: merge(cur.skills, next.skills),
        skills_not: merge(cur.skills_not, next.skills_not),
        grad: cur.grad || next.grad,
        university: cur.university || next.university,
        subject_families: merge(cur.subject_families, next.subject_families),
        subject_families_not: merge(cur.subject_families_not, next.subject_families_not),
        degree: cur.degree || next.degree,
        languages: merge(cur.languages, next.languages),
        languages_not: merge(cur.languages_not, next.languages_not),
        notable_extras: merge(cur.notable_extras, next.notable_extras),
        notable_extras_not: merge(cur.notable_extras_not, next.notable_extras_not),
        diversity_tags: merge(cur.diversity_tags, next.diversity_tags),
        diversity_tags_not: merge(cur.diversity_tags_not, next.diversity_tags_not),
        status: cur.status || next.status,
        cities: merge(cur.cities, next.cities),
        cities_not: merge(cur.cities_not, next.cities_not),
      }));
      toast.success(`Merged "${preset.name}" into current filters`);
    } else {
      setFilters(next);
      toast.success(`Loaded "${preset.name}"`);
    }
    setPresetPickerOpen(false);
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Give the preset a name"); return; }
    try {
      await api.post(`/firm-portal/${firm.slug}/filter-presets`, {
        name, filters,
      });
      toast.success(`Saved "${name}"`);
      setPresetSaveOpen(false);
      setPresetName("");
      reloadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deletePreset = async (id, name) => {
    try {
      await api.delete(`/firm-portal/${firm.slug}/filter-presets/${id}`);
      toast.success(`Deleted "${name}"`);
      reloadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Copy a shareable link that encodes the CURRENT filter grid
  // (including `_not` arrays and sort-by-fit toggle) into a compact
  // `?preset=…` param. The receiving firm-owner hits the same
  // auth-guarded URL and lands on the exact same result set.
  const copyShareableLink = async () => {
    const token = _encodePresetToken(filters, sortByFit);
    const url = new URL(window.location.href);
    url.searchParams.set("preset", token);
    // Also rewrite the browser bar so the recruiter can copy from
    // the address bar too. Uses replaceState (no history push).
    try { window.history.replaceState(null, "", url.toString()); } catch { /* noop */ }
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success("Shareable filter link copied");
    } catch {
      // Some browsers block clipboard.writeText without a secure
      // context; fall back to a prompt so the URL is at least
      // recoverable.
      try { window.prompt("Copy this shareable filter link:", url.toString()); }
      catch { toast.error("Copy blocked — try again"); }
    }
  };

  const openBroadcast = async () => {
    // Broadcast fans out to ONE tag at a time (rate-limit + cooldown
    // both scoped per-tag), so if the firm selected multiple tags we
    // require them to narrow to one before dispatching. This mirrors
    // the backend broadcast endpoint's single-tag contract.
    const picked = filters.diversity_tags || [];
    if (picked.length === 0) {
      toast.error("Pick a diversity focus first (in the filters above)");
      return;
    }
    if (picked.length > 1) {
      toast.error("Broadcast to one diversity focus at a time — narrow the filter to a single tag");
      return;
    }
    const tag = picked[0];
    setBroadcast({ ...broadcast, open: true, tag, preview: null });
    try {
      const { data } = await api.get("/talent/broadcast/preview", {
        params: { firm_slug: firm.slug, diversity_tag: tag },
      });
      setBroadcast((b) => ({ ...b, preview: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load preview");
      setBroadcast((b) => ({ ...b, open: false }));
    }
  };

  const sendBroadcast = async () => {
    if (!broadcast.message.trim() || broadcast.message.trim().length < 40) {
      toast.error("Message needs at least 40 characters so students know what they're being invited to");
      return;
    }
    setBroadcast((b) => ({ ...b, submitting: true }));
    try {
      const { data } = await api.post("/talent/broadcast", {
        firm_slug: firm.slug,
        diversity_tag: broadcast.tag,
        message: broadcast.message.trim(),
        programme_url: broadcast.programme_url.trim() || null,
      });
      toast.success(`Sent to ${data.recipient_count} students`);
      setBroadcast({ open: false, tag: "", message: "", programme_url: "", preview: null, submitting: false });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object"
        ? detail?.message || detail?.error
        : detail;
      toast.error(msg || "Broadcast failed");
      setBroadcast((b) => ({ ...b, submitting: false }));
    }
  };

  const interestByProfile = Object.fromEntries(
    (interests || []).map(i => [i.profile_id, i])
  );

  // ── Match-score ────────────────────────────────────────────────
  // Compare each candidate to the currently-active filters. Every
  // set filter contributes its weight; the score is the sum of
  // weights the candidate satisfies. Weighting matters once 3+
  // filters are active — a "hard" match (degree, family, uni,
  // sector) counts double so a Law + Consulting + London hit
  // beats a Law-only hit. Below 3 filters we fall back to
  // equal-weight (1 point each) because there's nothing to break
  // ties between.
  const scoreFilters = [];
  // Multi-value filters: score contribution = weight if the
  // candidate matches ANY selected value. Each filter counts once,
  // not once-per-value, so a firm can't game the ranking by
  // stacking sectors.
  const someLower = (arr, needles) =>
    (arr || []).some((x) => (needles || []).some(
      (n) => x.trim().toLowerCase() === n.trim().toLowerCase()
    ));
  if (filters.sectors?.length) scoreFilters.push({
    key: "sector", label: filters.sectors.join(" · "), weight: 2,
    hit: (r) => someLower(r.sectors, filters.sectors),
  });
  if (filters.skills?.length) scoreFilters.push({
    key: "skill", label: filters.skills.join(" · "), weight: 1,
    hit: (r) => someLower(r.skills, filters.skills),
  });
  if (filters.grad) scoreFilters.push({
    key: "grad", label: `Grad ${filters.grad}`, weight: 1,
    hit: (r) => String(r.graduation_year || "") === String(filters.grad),
  });
  if (filters.university?.id) scoreFilters.push({
    key: "university", label: filters.university.name, weight: 2,
    hit: (r) => r.university_id === filters.university.id,
  });
  if (filters.subject_families?.length) scoreFilters.push({
    key: "subject_family", label: filters.subject_families.join(" · "), weight: 3,
    // Backend already narrowed to the family set; mark returned
    // rows as hits so the filter always contributes to the score.
    hit: () => true,
  });
  if (filters.degree?.id) scoreFilters.push({
    key: "degree", label: filters.degree.title || filters.degree.id, weight: 3,
    hit: (r) => r.current_degree_id === filters.degree.id
      || (r.target_degree_ids || []).includes(filters.degree.id),
  });
  if (filters.languages?.length) scoreFilters.push({
    key: "language", label: filters.languages.join(" · "), weight: 1,
    hit: (r) => (r.languages || []).some(l => (filters.languages || []).some(
      (n) => (l.lang || "").trim().toLowerCase() === n.trim().toLowerCase()
    )),
  });
  if (filters.notable_extras?.length) scoreFilters.push({
    key: "notable_extra", label: filters.notable_extras.join(" · "), weight: 1,
    hit: (r) => (r.notable_extras || []).some(x => filters.notable_extras.includes(x)),
  });
  if (filters.diversity_tags?.length) scoreFilters.push({
    key: "diversity_tag", label: "diversity focus", weight: 1,
    hit: () => true,
  });
  if (filters.status) scoreFilters.push({
    key: "status", label: filters.status, weight: 1,
    hit: (r) => r.status === filters.status,
  });
  if (filters.cities?.length) scoreFilters.push({
    key: "city", label: filters.cities.join(" · "), weight: 2,
    hit: (r) => someLower(r.target_cities, filters.cities),
  });
  const activeCount = scoreFilters.length;
  const useWeights = activeCount >= 3;
  const maxScore = useWeights
    ? scoreFilters.reduce((n, f) => n + f.weight, 0)
    : activeCount;
  const scoreOf = (r) => scoreFilters.reduce(
    (n, f) => n + (f.hit(r) ? (useWeights ? f.weight : 1) : 0), 0
  );

  const sortedRows = sortByFit && maxScore > 0
    ? [...rows].sort((a, b) => scoreOf(b) - scoreOf(a))
    : rows;
  const mutuals = interests.filter(i => i.status === "mutual");

  if (locked) {
    return (
      <div
        className="p-6 rounded-xl border border-dashed border-stone-300 bg-stone-50"
        data-testid="firm-portal-talent-locked"
      >
        <div className="flex items-center gap-2 text-stone-500">
          <Lock className="h-4 w-4" />
          <span className="text-[11px] font-underground-sc tracking-widest">
            Mid / Large Verified Partner benefit
          </span>
        </div>
        <h3 className="mt-2 font-editorial text-2xl text-stone-900">
          Reverse Job Board
        </h3>
        <p className="mt-2 text-sm text-stone-600 max-w-2xl">
          Students opt in to be searchable by sector, skill and
          graduation year. Names and emails stay hidden until{" "}
          <span className="font-medium">both sides</span> say yes —
          then the intro is unlocked automatically.
        </p>
        <div className="mt-4 text-sm">
          <span className="text-stone-900 font-semibold">{aggregate.toLocaleString()}</span>{" "}
          <span className="text-stone-600">students currently available across the UK & US.</span>
        </div>
        <Button
          onClick={onGotoUpgrade}
          data-testid="firm-portal-talent-upgrade-cta"
          className="mt-4 bg-[#4C1D95] hover:bg-[#3B0764] text-white"
        >
          Upgrade to Mid or Large tier
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="firm-portal-talent-tab">
      <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/60">
        <div className="flex items-center gap-2 text-emerald-800">
          <Handshake className="h-4 w-4" />
          <span className="text-[10px] font-underground-sc tracking-widest">
            Reverse Job Board · Beta
          </span>
        </div>
        <p className="mt-1 text-sm text-stone-700">
          Every card is opt-in. When you request an intro and the
          student is separately interested in your firm, we reveal
          their name, email and CV to you automatically.
        </p>
      </div>

      {/* Mutual matches */}
      {mutuals.length > 0 && (
        <section className="rounded-xl border border-emerald-300 bg-white p-4">
          <div className="flex items-center gap-2 text-emerald-800 mb-2">
            <Sparkles className="h-4 w-4" />
            <span className="text-[10px] font-underground-sc tracking-widest">
              {mutuals.length} mutual match{mutuals.length === 1 ? "" : "es"}
            </span>
          </div>
          <ul className="space-y-2" data-testid="firm-portal-talent-mutuals">
            {mutuals.map((m) => (
              <li
                key={m.id}
                className="flex items-start justify-between gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200"
                data-testid={`firm-portal-mutual-${m.profile_id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-stone-900">
                    <UserRound className="h-3.5 w-3.5 text-emerald-700" />
                    {m.revealed?.name || "Anonymous"}
                  </div>
                  <div className="mt-0.5 text-[12px] text-stone-600 flex flex-wrap gap-3">
                    {m.revealed?.email && (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        <a
                          href={`mailto:${m.revealed.email}`}
                          className="text-emerald-800 underline break-all"
                        >
                          {m.revealed.email}
                        </a>
                      </span>
                    )}
                  </div>
                  {m.revealed?.diversity_tags?.length > 0 && (
                    <div
                      className="mt-1.5 flex flex-wrap gap-1"
                      data-testid={`firm-portal-mutual-diversity-${m.profile_id}`}
                    >
                      {m.revealed.diversity_tags.map((k) => (
                        <span
                          key={k}
                          className="inline-block rounded-full bg-[#4C1D95]/10 border border-[#4C1D95]/30 text-[#4C1D95] px-2 py-0.5 text-[10px]"
                        >
                          {DIVERSITY_LABELS[k] || k}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-1 text-[10px] font-mono text-stone-500">
                    Matched {fmt(m.matched_at)}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => withdrawIntro(m.id)}
                  data-testid={`firm-portal-mutual-withdraw-${m.profile_id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Saved-filter presets bar */}
      <div
        className="flex items-center gap-2 flex-wrap text-[12px]"
        data-testid="firm-portal-talent-presets-bar"
      >
        <span className="text-[10px] font-underground-sc tracking-widest text-stone-500">
          Presets:
        </span>
        <div className="relative">
          <button
            type="button"
            onClick={() => setPresetPickerOpen((v) => !v)}
            data-testid="firm-portal-talent-presets-picker-toggle"
            className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-3 py-1 hover:bg-stone-50"
          >
            {presets.length === 0 ? "No saved presets" : `Load a preset (${presets.length})`}
            <span className="ml-0.5 text-stone-400">▾</span>
          </button>
          {presetPickerOpen && presets.length > 0 && (
            <ul
              className="absolute z-30 mt-1 min-w-[240px] max-h-72 overflow-auto rounded-md border border-stone-200 bg-white shadow-lg"
              data-testid="firm-portal-talent-presets-menu"
            >
              {presets.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between px-3 py-1.5 hover:bg-stone-50"
                  data-testid={`firm-portal-talent-preset-${p.id}`}
                >
                  <button
                    type="button"
                    onClick={() => applyPreset(p)}
                    className="flex-1 text-left"
                    data-testid={`firm-portal-talent-preset-${p.id}-apply`}
                  >
                    {p.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => deletePreset(p.id, p.name)}
                    aria-label={`Delete ${p.name}`}
                    className="ml-2 text-stone-400 hover:text-rose-600"
                    data-testid={`firm-portal-talent-preset-${p.id}-delete`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={() => setPresetSaveOpen(true)}
          data-testid="firm-portal-talent-preset-save-open"
          className="inline-flex items-center gap-1 rounded-full border border-[#4C1D95] text-[#4C1D95] bg-white px-3 py-1 hover:bg-[#4C1D95]/10"
        >
          Save current filters
        </button>
        {/* Copy shareable link — serialises current filter grid
            (include + `_not` arrays + sort-by-fit) into ?preset=…
            base64 JSON so hiring managers can Slack a direct link
            to a specific candidate view. Recipient still hits the
            firm-owner auth guard first. */}
        <button
          type="button"
          onClick={copyShareableLink}
          data-testid="firm-portal-talent-preset-share-link"
          className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-3 py-1 hover:bg-stone-50 text-stone-700"
          title="Copy a link that reproduces these exact filters"
        >
          <Link2 className="h-3 w-3" />
          Copy shareable link
        </button>
        {/* Union toggle — when on, applying a preset MERGES its
            arrays into whatever's currently in the filter grid so
            you can stack "London · Law" ∪ "Manchester · Consulting"
            without hand-editing. Kept next to the picker toggle so
            it's obvious which button it affects. */}
        <label
          className="inline-flex items-center gap-1 cursor-pointer text-stone-600 select-none"
          data-testid="firm-portal-talent-preset-union"
          title="Union: apply next preset ON TOP of current filters (dedupes automatically)"
        >
          <input
            type="checkbox"
            checked={presetUnion}
            onChange={(e) => setPresetUnion(e.target.checked)}
            className="h-3.5 w-3.5 accent-[#4C1D95]"
            data-testid="firm-portal-talent-preset-union-input"
          />
          Union with current
        </label>
        {presetSaveOpen && (
          <div
            className="inline-flex items-center gap-1"
            data-testid="firm-portal-talent-preset-save-inline"
          >
            <Input
              autoFocus
              value={presetName}
              onChange={(e) => setPresetName(e.target.value.slice(0, 80))}
              placeholder='e.g. "London · Law · 2027"'
              className="h-8 w-64 text-[12px]"
              data-testid="firm-portal-talent-preset-name"
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); savePreset(); }
                else if (e.key === "Escape") { setPresetSaveOpen(false); setPresetName(""); }
              }}
            />
            <button
              type="button"
              onClick={savePreset}
              data-testid="firm-portal-talent-preset-save-submit"
              className="rounded-full bg-[#4C1D95] text-white px-3 py-1 text-[12px] hover:bg-[#3B0764]"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => { setPresetSaveOpen(false); setPresetName(""); }}
              className="text-stone-400 hover:text-stone-700 px-1"
              aria-label="Cancel save"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2"
        data-testid="firm-portal-talent-filters"
      >
        <MultiEnumPicker
          options={allowedSectors.map((s) => ({ key: s, label: SECTOR_LABELS[s] || s }))}
          values={filters.sectors}
          notValues={filters.sectors_not}
          onChange={(v) => setFilters({ ...filters, sectors: v })}
          {...handlersFor("sectors", "sectors_not")}
          placeholder="Any sector (pick 1+)"
          testId="firm-portal-talent-filter-sector"
        />
        <SuggestAutocomplete
          endpoint="/talent/skills/suggest"
          labelKey="skill"
          multi
          value={filters.skills}
          notValues={filters.skills_not}
          onChange={(v) => setFilters({ ...filters, skills: v })}
          {...handlersFor("skills", "skills_not")}
          placeholder="skills (start typing…)"
          testId="firm-portal-talent-filter-skill"
        />
        <Select
          value={filters.grad || "__any__"}
          onValueChange={(v) => setFilters({
            ...filters, grad: v === "__any__" ? "" : v,
          })}
        >
          <SelectTrigger
            className="w-full h-10"
            data-testid="firm-portal-talent-filter-grad"
          >
            <SelectValue placeholder="Any graduation year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Any graduation year</SelectItem>
            {(() => {
              const thisYear = new Date().getFullYear();
              const years = [];
              for (let y = thisYear - 5; y <= thisYear + 4; y++) years.push(y);
              return years.map((y) => (
                <SelectItem
                  key={y}
                  value={String(y)}
                  data-testid={`firm-portal-talent-filter-grad-opt-${y}`}
                >
                  {y}
                </SelectItem>
              ));
            })()}
          </SelectContent>
        </Select>
        <UniversityAutocomplete
          value={filters.university}
          onSelect={(u) => setFilters({
            ...filters,
            university: u,
            // Course filter is scoped to a chosen uni; clear it when
            // the uni changes so we never carry a stale degree id
            // that belongs to a different institution.
            degree: u?.id === filters.university?.id ? filters.degree : null,
          })}
          placeholder="university (autocomplete)"
          testIdPrefix="firm-portal-talent-filter-uni"
        />
        {/* Course-level filter — disabled until a university is
            picked (DegreeAutocomplete requires universityId). */}
        <DegreeAutocomplete
          universityId={filters.university?.id}
          value={filters.degree}
          onSelect={(d) => setFilters({ ...filters, degree: d })}
          placeholder={filters.university?.id
            ? "course (e.g. PPE)"
            : "pick a university first"}
          testIdPrefix="firm-portal-talent-filter-degree"
        />
        <MultiEnumPicker
          options={subjectFamilies}
          values={filters.subject_families}
          notValues={filters.subject_families_not}
          onChange={(v) => setFilters({ ...filters, subject_families: v })}
          {...handlersFor("subject_families", "subject_families_not")}
          placeholder="Any degree family (pick 1+)"
          testId="firm-portal-talent-filter-subject-family"
        />
        <SuggestAutocomplete
          endpoint="/talent/languages/suggest"
          labelKey="language"
          multi
          value={filters.languages}
          notValues={filters.languages_not}
          onChange={(v) => setFilters({ ...filters, languages: v })}
          {...handlersFor("languages", "languages_not")}
          placeholder="languages (start typing…)"
          testId="firm-portal-talent-filter-language"
        />
        <MultiEnumPicker
          options={allowedNotable.map((k) => ({ key: k, label: NOTABLE_LABELS[k] || k }))}
          values={filters.notable_extras}
          notValues={filters.notable_extras_not}
          onChange={(v) => setFilters({ ...filters, notable_extras: v })}
          {...handlersFor("notable_extras", "notable_extras_not")}
          placeholder="Any notable extra (pick 1+)"
          testId="firm-portal-talent-filter-notable"
        />
        <MultiEnumPicker
          options={allowedDiversity.map((k) => ({ key: k, label: DIVERSITY_LABELS[k] || k }))}
          values={filters.diversity_tags}
          notValues={filters.diversity_tags_not}
          onChange={(v) => setFilters({ ...filters, diversity_tags: v })}
          {...handlersFor("diversity_tags", "diversity_tags_not")}
          placeholder="Any diversity focus (opt-in only)"
          testId="firm-portal-talent-filter-diversity"
        />
        {filters.diversity_tags?.length > 0 && (
          <button
            type="button"
            onClick={openBroadcast}
            data-testid="firm-portal-talent-broadcast-open"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#4C1D95] bg-[#4C1D95] text-white px-3 py-1.5 text-[12px] hover:bg-[#3B0764]"
            title={filters.diversity_tags.length === 1
              ? "Send one invite to every opt-in student with this tag"
              : "Narrow to a single diversity focus to broadcast"}
          >
            <Megaphone className="h-3.5 w-3.5" />
            {filters.diversity_tags.length === 1
              ? "Invite all with this focus"
              : `Invite (narrow to 1 focus)`}
          </button>
        )}
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="h-10 rounded-md border border-stone-300 bg-white px-2 text-sm"
          data-testid="firm-portal-talent-filter-status"
        >
          <option value="">Current or applying</option>
          <option value="current">Currently at university</option>
          <option value="applying">Applying to university</option>
        </select>
        <SuggestAutocomplete
          endpoint="/cities/suggest"
          labelKey="name"
          multi
          value={filters.cities}
          notValues={filters.cities_not}
          onChange={(v) => setFilters({ ...filters, cities: v })}
          {...handlersFor("cities", "cities_not")}
          placeholder="target cities (start typing…)"
          testId="firm-portal-talent-filter-city"
        />
      </div>
      <FilterChipsSummary
        filters={filters}
        setFilters={setFilters}
        allowedSectors={allowedSectors}
        subjectFamilies={subjectFamilies}
        allowedNotable={allowedNotable}
        allowedDiversity={allowedDiversity}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          data-testid="firm-portal-talent-search"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Search
        </Button>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => setSortByFit((v) => !v)}
            data-testid="firm-portal-talent-sort-fit"
            className={
              "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[12px] transition " +
              (sortByFit
                ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                : "bg-white text-[#4C1D95] border-[#4C1D95] hover:bg-[#4C1D95]/10")
            }
            title={useWeights
              ? `Sort by best fit across ${activeCount} filters (weighted — degree, family, uni, sector count more)`
              : `Sort by best fit across ${activeCount} active filter${activeCount === 1 ? "" : "s"}`}
          >
            Sort by best fit · {activeCount} filter{activeCount === 1 ? "" : "s"}
            {useWeights && <span className="ml-1 text-[10px] opacity-70">weighted</span>}
            {sortByFit && <span className="opacity-80 ml-1">×</span>}
          </button>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-stone-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
          Loading talent…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="p-8 text-center border border-dashed border-stone-300 rounded-xl text-stone-500"
          data-testid="firm-portal-talent-empty"
        >
          No students match this filter yet. Try removing filters — new
          profiles land here every week.
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-3" data-testid="firm-portal-talent-grid">
          {sortedRows.map((r) => {
            const already = interestByProfile[r.id];
            const isMutual = already?.status === "mutual";
            const score = activeCount > 0 ? scoreOf(r) : 0;
            return (
              <li
                key={r.id}
                className="p-4 rounded-xl border border-stone-200 bg-white"
                data-testid={`firm-portal-talent-card-${r.id}`}
              >
                <div className="flex items-center justify-between text-[11px] font-mono text-stone-500">
                  <span>Grad {r.graduation_year || "—"}</span>
                  <div className="flex items-center gap-2">
                    {activeCount > 0 && (
                      <span
                        className={
                          "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border " +
                          (score === maxScore
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : score >= Math.ceil(maxScore * 0.66)
                              ? "bg-amber-100 text-amber-900 border-amber-300"
                              : "bg-stone-100 text-stone-700 border-stone-300")
                        }
                        data-testid={`firm-portal-talent-card-${r.id}-fit`}
                        title={useWeights
                          ? `Weighted score ${score} of ${maxScore} — degree, family, uni, sector count more`
                          : `Matches ${score} of ${maxScore} active filters`}
                      >
                        {score}/{maxScore} fit
                      </span>
                    )}
                    <span>{fmt(r.created_at)}</span>
                  </div>
                </div>
                <div className="mt-1 font-medium text-stone-900 text-sm truncate">
                  {r.course_hint || (r.status === "applying" ? "Applying" : "Course not specified")}
                </div>
                {r.status && (
                  <div className="mt-0.5 text-[10px] font-underground-sc tracking-widest text-stone-500">
                    {r.status === "current" ? "Currently at university" : "Applying"}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {(r.sectors || []).map((s) => (
                    <Badge key={s} variant="outline" className="text-[10px] py-0 px-1.5">
                      {s}
                    </Badge>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(r.skills || []).map((s) => (
                    <span
                      key={s}
                      className="text-[11px] rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-amber-900"
                    >
                      {s}
                    </span>
                  ))}
                </div>
                {(r.target_cities || []).length > 0 && (
                  <div className="mt-2 flex items-center gap-1 flex-wrap text-[11px] text-emerald-800">
                    <span className="font-mono text-stone-400">Targeting</span>
                    {(r.target_cities || []).map((c) => (
                      <span key={c} className="rounded bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
                {(r.languages || []).length > 0 && (
                  <div className="mt-2 flex items-center gap-1 flex-wrap text-[11px] text-stone-500">
                    <Globe className="h-3 w-3" />
                    {(r.languages || []).map((l, k) => (
                      <span key={k} className="rounded bg-stone-100 border border-stone-200 px-1.5 py-0.5">
                        {l.lang} · {l.level}
                      </span>
                    ))}
                  </div>
                )}
                {(r.notable_extras || []).length > 0 && (
                  <div className="mt-2 flex items-center gap-1 flex-wrap text-[11px] text-amber-900">
                    <Award className="h-3 w-3 text-amber-600" />
                    {(r.notable_extras || []).map((k) => {
                      const isFilteredMatch = filters.notable_extra && k === filters.notable_extra;
                      return (
                        <span
                          key={k}
                          data-testid={`firm-portal-talent-card-${r.id}-extra-${k}`}
                          className={
                            "rounded px-1.5 py-0.5 border " +
                            (isFilteredMatch
                              ? "bg-amber-500 border-amber-600 text-white font-medium"
                              : "bg-amber-50 border-amber-200 text-amber-900")
                          }
                        >
                          {NOTABLE_LABELS[k] || k.replaceAll("_", " ")}
                        </span>
                      );
                    })}
                    {(r.notable_extras || []).length >= 4 && (
                      <span
                        className="rounded-full bg-[#4C1D95] text-white px-2 py-0.5 text-[10px] font-medium"
                        data-testid={`firm-portal-talent-card-${r.id}-well-rounded`}
                        title="Well-rounded profile — 4+ notable extras"
                      >
                        Well-rounded
                      </span>
                    )}
                  </div>
                )}
                <p className="mt-2 text-[12px] text-stone-600 leading-relaxed line-clamp-3">
                  {r.bio}
                </p>
                {r.linkedin_url && (
                  <a
                    href={r.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#0A66C2] hover:underline"
                    data-testid={`firm-portal-talent-linkedin-${r.id}`}
                  >
                    <Linkedin className="h-3 w-3" /> LinkedIn
                  </a>
                )}
                <div className="mt-3">
                  {isMutual ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                      Mutual match — see above
                    </Badge>
                  ) : already ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => withdrawIntro(already.id)}
                      data-testid={`firm-portal-talent-withdraw-${r.id}`}
                    >
                      Cancel intro request
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => requestIntro(r.id)}
                      className="bg-[#4C1D95] hover:bg-[#3B0764] text-white"
                      data-testid={`firm-portal-talent-request-${r.id}`}
                    >
                      Request intro
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Broadcast compose modal */}
      {broadcast.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="firm-portal-talent-broadcast-modal"
          onClick={() => !broadcast.submitting && setBroadcast({ ...broadcast, open: false })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white rounded-2xl border border-stone-200 shadow-xl p-5 space-y-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#4C1D95]">
                <Megaphone className="h-4 w-4" />
                <span className="text-[10px] font-underground-sc tracking-widest">
                  Broadcast to {DIVERSITY_LABELS[broadcast.tag] || broadcast.tag}
                </span>
              </div>
              <button
                onClick={() => setBroadcast({ ...broadcast, open: false })}
                disabled={broadcast.submitting}
                className="text-stone-400 hover:text-stone-700 text-2xl leading-none"
                aria-label="Close"
              >×</button>
            </div>

            {!broadcast.preview ? (
              <div className="text-stone-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading preview…
              </div>
            ) : broadcast.preview.in_cooldown ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900">
                You've already broadcast to this tag in the last 14 days.
                Next send available: <span className="font-mono">{new Date(broadcast.preview.next_send_at).toLocaleString()}</span>.
              </div>
            ) : broadcast.preview.recipient_count === 0 ? (
              <div className="rounded-md border border-stone-300 bg-stone-50 p-3 text-[13px] text-stone-700">
                No students are currently opted in for this tag. Once
                more students join, they'll be reachable here.
              </div>
            ) : (
              <>
                <p className="text-[13px] text-stone-600 leading-relaxed">
                  This will send one email + in-app invite to
                  <span className="font-medium text-stone-900"> {broadcast.preview.recipient_count} student{broadcast.preview.recipient_count === 1 ? "" : "s"}</span>
                  {" "}who both hold this tag AND opted in to firm outreach.
                  If they mirror your interest, contacts reveal on both sides.
                </p>
                <label className="block">
                  <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
                    Programme URL (optional)
                  </div>
                  <Input
                    value={broadcast.programme_url}
                    onChange={(e) => setBroadcast({ ...broadcast, programme_url: e.target.value })}
                    placeholder="https://firm.com/programme"
                    data-testid="firm-portal-talent-broadcast-url"
                  />
                </label>
                <label className="block">
                  <div className="flex justify-between text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
                    <span>Message ({broadcast.message.length}/800 · min 40)</span>
                  </div>
                  <textarea
                    rows={6}
                    value={broadcast.message}
                    onChange={(e) => setBroadcast({ ...broadcast, message: e.target.value.slice(0, 800) })}
                    placeholder={"e.g. Our Women in Investment Banking Insight Programme runs 12–14 April. Two days at our London office with real desk shadowing…"}
                    className="w-full rounded-md border border-stone-300 bg-white p-2 text-sm outline-none focus:border-[#4C1D95] focus:ring-2 focus:ring-[#4C1D95]/20"
                    data-testid="firm-portal-talent-broadcast-message"
                  />
                </label>
                <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
                  <Button
                    variant="ghost"
                    onClick={() => setBroadcast({ ...broadcast, open: false })}
                    disabled={broadcast.submitting}
                    data-testid="firm-portal-talent-broadcast-cancel"
                  >Cancel</Button>
                  <Button
                    onClick={sendBroadcast}
                    disabled={broadcast.submitting || broadcast.message.trim().length < 40}
                    className="bg-[#4C1D95] hover:bg-[#3B0764] text-white"
                    data-testid="firm-portal-talent-broadcast-send"
                  >
                    {broadcast.submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Megaphone className="h-4 w-4 mr-1" />}
                    Send to {broadcast.preview.recipient_count}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
